/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useMemo } from "react";
// AG Grid packages and required enterprise features
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise"; // Import enterprise package for licensing
import "../../styles/components/VariantTable.css";
import "../../styles/components/SideBar.css";

// Hooks and Services
import { useVariantGrid } from "./hooks";
import { useVariantFilters } from "../filtering/hooks";
import { variantService } from "./services/variantService";
import AttributeFilterParser from "../../utils/attributeFilterParser";

// Components
import { ResizableSidebar, MainContentPanel } from "../ui";
import { FilterEditProvider } from "../filtering/contexts/FilterEditContext";

const VariantBrowserPage = ({ token, sidebarVisible, toggleSidebar }) => {
  // UI State
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [showTable, setShowTable] = useState(0);
  const [vepDynamic, setVepDynamic] = useState(true);
  const [showActiveFilters, setShowActiveFilters] = useState(true);

  // Attribute filter string state for bidirectional sync
  const [attributeFilterString, setAttributeFilterString] = useState("");

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [rowIndex, setRowIndex] = useState(0);

  // Custom Hooks - grid first, then filter with callbacks
  const gridHook = useVariantGrid(token, { Annotations: vepDynamic });
  const {
    columnDefs,
    filter_results,
    group_counts,
    gridRef,
    defaultColDef,
    onColumnGroupOpened,
    onAdvancedFilterBuilderVisibleChanged,
    showBuilder,
    refreshCache,
    onGridReady,
    setColumnFromData,
    setFilterResult,
    toggleColumnGroup,
  } = gridHook;


  const filterHook = useVariantFilters(token, refreshCache, setFilterResult);
  const {
    filterData,
    setFilterData,
    filters,
    setFilters,
    filterMessage,
    setFilterMessage,
    selectedDiseases,
    setSelectedDiseases,
    DiseaseDatasets,
    setDiseaseDatasets,
    confidence_score,
    setConfidenceScore,
    selectedPanels,
    setSelectedPanels,
    addFilter,
    clearFilters,
    deleteFilter,
    updateFilterLogic,
  } = filterHook;

  // Set up grid initialization callback
  useEffect(() => {
    window.initializeGridCallback = (result) => {
      if (setColumnFromData) {
        setColumnFromData(result, setRowIndex);
      }
    };

    return () => {
      delete window.initializeGridCallback;
    };
  }, [setColumnFromData, setRowIndex]);


  // Initialize attribute filter parser
  const attributeParser = useMemo(
    () => new AttributeFilterParser(gridRef),
    [gridRef]
  );

  // Handle AG-Grid filter changes - convert to string and update text input
  const onFilterChanged = useCallback(() => {
    if (!gridRef.current?.api) return;

    const advancedFilterModel = gridRef.current.api.getAdvancedFilterModel();

    if (advancedFilterModel) {
      // Serialize AG-Grid model to string for display in text input
      try {
        const filterString = attributeParser.serialize(advancedFilterModel);
        setAttributeFilterString(filterString);
      } catch (error) {
        console.warn("Failed to serialize filter model:", error);
        setAttributeFilterString("");
      }
    } else {
      // Clear string when no filter
      setAttributeFilterString("");
    }
  }, [gridRef, attributeParser, setAttributeFilterString]);

  // Handle VEP dynamic changes
  useEffect(() => {
    // Wait for grid to be ready before switching VEP
    if (!gridRef.current?.api) {
      return;
    }
    const switchVepDynamic = async () => {
      try {
        await variantService.switchVepDynamic(token, vepDynamic);
        toggleColumnGroup("Annotations", vepDynamic);

        // Clear cache when switching VEP ON to fetch fresh data with annotations
        if (vepDynamic) {
          refreshCache();
        }
      } catch (error) {
        console.error("Error switching VEP dynamic:", error);
      }
    };
    switchVepDynamic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vepDynamic, token, toggleColumnGroup, refreshCache, gridRef]);


  // Event Handlers
  const handleFormToggle = (formType) => {
    setShowAnnotator(formType === "dynamic");
  };

  const handleTableSwitch = useCallback((tableIndex) => {
    setShowTable(tableIndex);
  }, []);

  const toggleActiveFilters = useCallback(() => {
    setShowActiveFilters((prev) => !prev);
  }, []);

  return (
    <FilterEditProvider
      setFilterData={setFilterData}
      setFilters={setFilters}
      setFilterMessage={setFilterMessage}
      gridRef={gridRef}
      setAttributeFilterString={setAttributeFilterString}
    >
      <div
        className="variant-browser-container"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="d-flex flex-grow-1"
          style={{ margin: 0, height: "100%" }}
        >
          <ResizableSidebar
            sidebarProps={{
              token,
              showAnnotator,
              vepDynamic,
              setVepDynamic,
              filterData,
              setFilterData,
              filters,
              setFilters,
              addFilter,
              showModal,
              setShowModal,
              showPanel,
              setShowPanel,
              selectedDiseases,
              setSelectedDiseases,
              confidence_score,
              setConfidenceScore,
              DiseaseDatasets,
              setDiseaseDatasets,
              selectedPanels,
              setSelectedPanels,
              rowIndex,
              showBuilder,
              handleFormToggle,
              sidebarVisible,
              toggleSidebar,
              filter_results,
              gridRef,
              attributeFilterString,
              setAttributeFilterString,
            }}
          />

          <MainContentPanel
            token={token}
            showTable={showTable}
            handleTableSwitch={handleTableSwitch}
            filterMessage={filterMessage}
            setFilterMessage={setFilterMessage}
            filter_results={filter_results}
            group_counts={group_counts}
            clearFilters={clearFilters}
            deleteFilter={(filterType) =>
              deleteFilter(filterType, filter_results)
            }
            updateFilterLogic={updateFilterLogic}
            columnDefs={columnDefs}
            gridRef={gridRef}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
            onAdvancedFilterBuilderVisibleChanged={
              onAdvancedFilterBuilderVisibleChanged
            }
            onFilterChanged={onFilterChanged}
            onColumnGroupOpened={onColumnGroupOpened}
            refreshCache={refreshCache}
            sidebarVisible={sidebarVisible}
            showActiveFilters={showActiveFilters}
            toggleActiveFilters={toggleActiveFilters}
            onGridInitialization={setColumnFromData}
          />
        </div>
      </div>
    </FilterEditProvider>
  );
};

export default VariantBrowserPage;
